import {MouseEvent, useMemo} from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {GridColDef} from "@mui/x-data-grid";
import {useNavigate, useSearchParams} from "react-router-dom";
import {Box, Chip, Colors, IconButton, Text} from "@bounce/shared/ui";
import {Button, ListDataGrid, RowActions} from "../../ui/components";
import {useGetAudiences} from "../../apis/requests/audiences/getAudiences";
import {AudienceRow} from "../../apis/requests/audiences/types";
import {useGetTemplatesBySegments} from "../../apis/requests/templates/getTemplatesBySegments";
import {ReachTemplatesRead} from "../../apis/requests/templates/types";
import {reachUrls} from "../../layout/menu-items";
import {CHANNEL_DISPLAY, ChipVariant, STATUS_DISPLAY} from "../templates/templateDisplay";

const sameIds = (a: number[], b: number[]): boolean => a.length === b.length && a.every((id) => b.includes(id));

const AudienceDetailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {data, isLoading, error} = useGetAudiences();
  const audiences = data ?? [];

  const segmentIds = useMemo(() => searchParams.getAll("segment_ids").map(Number), [searchParams]);

  const audience = useMemo<AudienceRow | undefined>(
    () => audiences.find((row) => sameIds(row.audience_segment_ids, segmentIds)),
    [audiences, segmentIds]
  );

  const {data: campaignTemplates = [], isLoading: templatesLoading} = useGetTemplatesBySegments(
    {segmentIds: audience?.campaign_segment_ids ?? []},
    {enabled: !!audience}
  );

  const columns = useMemo<GridColDef<ReachTemplatesRead>[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 2,
        renderCell: ({row}) => (
          <Text
            size={14}
            variant="inter600"
            sx={{color: Colors.grey._900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.name}
          </Text>
        ),
      },
      {
        field: "content_channel",
        headerName: "Channel",
        flex: 1,
        renderCell: ({row}) => {
          const display = CHANNEL_DISPLAY[row.content_channel] ?? {
            label: row.content_channel,
            variant: "inherit" as ChipVariant,
          };
          return <Chip size_="small" variant_={display.variant} label={display.label} />;
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        renderCell: ({row}) => {
          const display = STATUS_DISPLAY[row.status] ?? {label: row.status, variant: "inherit" as ChipVariant};
          return <Chip size_="small" variant_={display.variant} label={display.label} />;
        },
      },
      {
        field: "principal",
        headerName: "Created by",
        flex: 1,
        renderCell: ({row}) => (
          <Text
            size={14}
            sx={{color: Colors.grey._700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.principal ? row.principal.split("@")[0] : ""}
          </Text>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 72,
        sortable: false,
        filterable: false,
        renderCell: ({row}) => <RowActions onOpenInNewTab={() => window.open(`/templates/${row.id}`, "_blank")} />,
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <Box sx={{p: 4}}>
        <Text size={14}>Loading…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{p: 4}}>
        <Text size={14}>Failed to load audiences.</Text>
      </Box>
    );
  }

  if (!audience) {
    return (
      <Box sx={{p: 4, display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start"}}>
        <Text size={14}>Audience not found.</Text>
        <Button buttonType="outlined-primary" size="small" onClick={() => navigate(reachUrls.audiences)}>
          Back to audiences
        </Button>
      </Box>
    );
  }

  const onCreateTemplate = (event: MouseEvent) => {
    const newTemplateUrl = `${reachUrls.templatesNew}?segment_ids=${audience.campaign_segment_ids.join(",")}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(newTemplateUrl, "_blank");
    } else {
      navigate(newTemplateUrl);
    }
  };

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3}}>
        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
          <IconButton
            onClick={() => navigate(reachUrls.audiences)}
            sx={{
              border: `1px solid ${Colors.tertiary._300}`,
              borderRadius: "8px",
              padding: "6px",
              color: Colors.grey._700,
              "&:hover": {backgroundColor: Colors.tertiary._100},
            }}
          >
            <ArrowBackIcon sx={{fontSize: 20}} />
          </IconButton>
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center"}}>
            {audience.audience_segment_names.length === 0 ? (
              <Chip size_="small" variant_="inherit" label="Unassigned" />
            ) : (
              audience.audience_segment_names.map((name, i) => (
                <Chip key={i} size_="small" variant_="secondary" label={name} />
              ))
            )}
          </Box>
        </Box>
        <Button buttonType="contained-primary" size="small" onClick={onCreateTemplate}>
          + New template
        </Button>
      </Box>

      <Box sx={{display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap", mb: 3}}>
        <Text size={14} sx={{color: Colors.grey._700}}>
          {audience.account_count.toLocaleString()} accounts
        </Text>
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
          <Text size={14} sx={{color: Colors.grey._600}}>
            Campaign:
          </Text>
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center"}}>
            {audience.campaign_segment_names.length === 0 ? (
              <Chip size_="small" variant_="inherit" label="Default" />
            ) : (
              audience.campaign_segment_names.map((name, i) => (
                <Chip key={i} size_="small" variant_="inherit" label={name} />
              ))
            )}
          </Box>
        </Box>
      </Box>

      <Text size={16} variant="interDisplay600" sx={{color: Colors.grey._700, mb: 1.5}}>
        Templates
      </Text>
      <ListDataGrid
        rows={campaignTemplates}
        columns={columns}
        loading={templatesLoading}
        getRowId={(row) => row.id}
        initialState={{sorting: {sortModel: [{field: "name", sort: "asc"}]}}}
        getRowUrl={(row) => `/templates/${row.id}`}
        noRowsText="No templates in this campaign yet"
      />
    </Box>
  );
};

export default AudienceDetailPage;
